<template>
  <transition name="voip-notification-fade">
    <div v-if="isVisible" class="voip-call-notification">
      <div class="notification-content">
        <div class="notification-icon">
          <i class="el-icon-phone"></i>
        </div>
        <div class="notification-info">
          <div class="notification-title">{{ callStatusText }}</div>
          <div class="notification-phone">{{ currentPhone }}</div>
          <div :class="['notification-timer', callStatus === 'connected' ? 'show' : '']">{{ formattedTime }}</div>
        </div>
        <div class="notification-actions">
          <button class="hangup-btn" @click="hangup" title="挂断电话">
            <i class="el-icon-switch-button"></i>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import { hungup, onHungup, onError, offHungup, offError, onAnswered, offAnswered, onTrying, offTrying, onRinging, offRinging } from "@/utils/appSignalRService.voip";

export default {
  name: "VoipCallNotification",
  data() {
    return {
      timer: null,
      callDuration: 0, // 通话时长（毫秒）
      callStatus: 'trying', // 通话状态：trying(呼叫中) | ringing(响铃中) | connected(通话中)
    };
  },
  computed: {
    ...mapState("windState", ["currentCallPhone", "callStartTime"]),
    isVisible() {
      return !!this.currentCallPhone;
    },
    currentPhone() {
      return this.currentCallPhone || "";
    },
    callStatusText() {
      switch (this.callStatus) {
        case 'ringing':
          return "响铃中";
        case 'connected':
          return "通话中";
        default:
          return "呼叫中";
      }
    },
    formattedTime() {
      const hours = Math.floor(this.callDuration / 3600000);
      const minutes = Math.floor((this.callDuration % 3600000) / 60000);
      const seconds = Math.floor((this.callDuration % 60000) / 1000);
      return `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
    },
  },
  mounted() {
    // 监听电话响铃事件
    onRinging(this.handleRinging);
    // 监听通话正在呼叫事件
    onTrying(this.handleTrying);
    // 监听通话已应答事件
    onAnswered(this.handleAnswered);
    // 监听通话挂断事件
    onHungup(this.handleHungup);
    // 监听错误事件
    onError(this.handleError);
    // 组件挂载时启动计时器（如果已有通话）
    if (this.currentCallPhone && this.callStartTime) {
      this.startTimer();
    }
  },
  beforeDestroy() {
    // 组件销毁时停止计时器
    this.stopTimer();
    // 解绑 SignalR 事件监听器
    offRinging(this.handleRinging);
    offTrying(this.handleTrying);
    offAnswered(this.handleAnswered);
    offHungup(this.handleHungup);
    offError(this.handleError);
  },
  methods: {
    ...mapMutations("windState", {
      setCurrentCallPhone: "SET_CURRENT_CALL_PHONE",
      clearCurrentCallPhone: "CLEAR_CURRENT_CALL_PHONE",
      setCallStartTime: "SET_CALL_START_TIME",
      clearCallStartTime: "CLEAR_CALL_START_TIME",
    }),
    pad(num) {
      return num.toString().padStart(2, "0");
    },
    startTimer() {
      this.stopTimer();
      // 初始化通话时长
      this.callDuration = Date.now() - this.callStartTime;
      // 启动计时器，每秒更新
      this.timer = setInterval(() => {
        this.callDuration = Date.now() - this.callStartTime;
        this.$forceUpdate();
      }, 1000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // 处理电话响铃
    handleRinging(phoneNum) {
      console.log("电话响铃:", phoneNum);
      // 更新通话状态为响铃中
      this.callStatus = 'ringing';
      // 设置呼叫的电话号码
      this.setCurrentCallPhone(phoneNum);
    },
    // 处理通话正在呼叫
    handleTrying() {
      console.log("通话正在呼叫:");
      // 更新通话状态为呼叫中
      this.callStatus = 'trying';
      // 注意：这里不设置通话开始时间，等待 Answered 后再设置
    },
    // 处理通话已应答
    handleAnswered() {
      console.log("通话已应答:");
      // 更新通话状态为通话中
      this.callStatus = 'connected';
      // 设置通话开始时间（从接听时开始计时）
      this.setCallStartTime(Date.now());
      // 开始计时
      this.startTimer();
    },
    // 处理通话挂断
    handleHungup(data) {
      console.log("通话已挂断:", data);
      this.stopTimer();
      this.clearCurrentCallPhone();
      this.clearCallStartTime();
    },
    // 处理错误
    handleError(error) {
      console.error("VoIP 通话错误:", error);
      this.stopTimer();
      this.clearCurrentCallPhone();
      this.clearCallStartTime();
    },
    async hangup() {
      try {
        await hungup();
        this.stopTimer();
        this.clearCurrentCallPhone();
        this.clearCallStartTime();
      } catch (error) {
        console.error("挂断电话失败:", error);
        this.$message.error("挂断电话失败，请稍后重试");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.voip-notification-fade {

  &-enter-active,
  &-leave-active {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &-enter,
  &-leave-to {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
  }

  &-leave-active {
    position: absolute;
  }
}

.voip-call-notification {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;

  .notification-content {
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, rgba(18, 117, 220, 0.95), rgba(64, 244, 144, 0.95));
    border-radius: 50px;
    padding: 12px 24px 12px 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);

    .notification-icon {
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;

      i {
        color: #fff;
        font-size: 20px;
      }
    }

    .notification-info {
      flex: 1;
      min-width: 0;
      margin-right: 16px;

      .notification-title {
        color: #fff;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 4px;
        display: flex;
        align-items: center;

        &::before {
          content: "";
          display: inline-block;
          width: 8px;
          height: 8px;
          background: #fff;
          border-radius: 50%;
          margin-right: 8px;
          animation: pulse 1.5s ease-in-out infinite;
        }
      }

      .notification-phone {
        color: rgba(255, 255, 255, 0.9);
        font-size: 14px;
        margin-bottom: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .notification-timer {
        color: #fff;
        font-size: 18px;
        font-family: "Courier New", monospace;
        font-weight: bold;
        letter-spacing: 1px;
        height: 24px;
        visibility: hidden;
      }

      .notification-timer.show {
        visibility: visible;
      }
    }

    .notification-actions {
      .hangup-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(255, 87, 87, 0.9);
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 0;

        i {
          font-size: 18px;
          color: #fff;
        }

        &:hover {
          background: rgba(255, 87, 87, 1);
          transform: scale(1.05);
          box-shadow: 0 0 12px rgba(255, 87, 87, 0.6);
        }

        &:active {
          transform: scale(0.95);
        }
      }
    }
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}
</style>
